/**
 * Sea navigation — obstacle-aware route planning for the tap-to-sail autopilot (and any future NPC
 * captain). Pure + deterministic: no wall-clock, no Math.random, fixed iteration orders and strict
 * tie-breaking everywhere, so identical inputs always produce byte-identical routes (unit-testable, and
 * safe to reuse server-side later). AUTHORITY NOTE: today this only synthesises INPUT — the client's
 * autopilot steers along the returned route via the same C_INPUT path a thumb uses; the server remains
 * the sole authority on where the hull actually goes.
 *
 * Algorithm: A* over a coarse square grid whose cells are blocked by the SAME solid circles the sim
 * collides with (`collideCircles` obstacles), inflated by (hull radius + planning margin) — followed by a
 * string-pulling smoothing pass (greedy line-of-sight against the exact circles), so the final route is a
 * handful of straight sailor legs, each PROVEN clear at full clearance, never a grid staircase. The grid
 * only decides topology (which side of an island, which channel); the geometry is exact.
 *
 * Rasterisation is conservative (cell SQUARE vs inflated circle): a free cell's entire square is clear of
 * every margin, so the segment between two adjacent free cell centres is provably clear too — smoothing can
 * always advance at least one hop and can never wedge. The trade-off: a channel narrower than ~a cell may
 * read closed and the route goes around it — exactly what a prudent sailor does.
 */
import type { Obstacle } from './ship.js';

export interface NavPoint { x: number; y: number }

/** Distance from point (px,py) to the segment a→b (closed form, allocation-free). */
export function pointSegDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  if (t < 0) t = 0; else if (t > 1) t = 1;
  const cx = ax + t * dx, cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

/**
 * True when the segment a→b keeps at least `clearance` beyond every obstacle's radius — with a GRACE for
 * circles the START already sits inside the margin of (a hull collide-pushed onto a shore, or skimming it):
 * for those, the leg may not approach CLOSER than the start already is (95% of it, so tangential skimming
 * along the shore never false-blocks), but it can never dive toward the core. With the start in open water
 * this is the plain strict test.
 */
export function segmentClearFrom(
  ax: number, ay: number, bx: number, by: number,
  obstacles: readonly Obstacle[], clearance: number,
): boolean {
  for (let i = 0; i < obstacles.length; i++) {
    const o = obstacles[i]!;
    const startGap = Math.hypot(ax - o.x, ay - o.y) - o.r;   // how far outside the core the start sits
    const eff = startGap < clearance ? Math.max(0, startGap) * 0.95 : clearance;
    if (pointSegDist(o.x, o.y, ax, ay, bx, by) < o.r + eff) return false;
  }
  return true;
}

/**
 * Project a point into navigable water: push it OUT of every inflated circle (a tap ON an island becomes
 * the nearest safe water off its shore, biased toward `towardX/Y` — the ship — when the tap sits at the
 * exact centre) and clamp it `inset` inside the world square. Iterated a few passes so overlapping margins
 * settle. Pure; returns a fresh point.
 */
export function projectToWater(
  x: number, y: number, obstacles: readonly Obstacle[], clearance: number,
  size: number, inset: number, towardX: number, towardY: number,
): NavPoint {
  let px = Math.min(Math.max(x, inset), size - inset);
  let py = Math.min(Math.max(y, inset), size - inset);
  for (let pass = 0; pass < 4; pass++) {
    let moved = false;
    for (let i = 0; i < obstacles.length; i++) {
      const o = obstacles[i]!;
      const min = o.r + clearance;
      let dx = px - o.x, dy = py - o.y;
      let d = Math.hypot(dx, dy);
      if (d >= min) continue;
      if (d < 1e-4) { // tap at the dead centre — step off toward the ship (or +x when the ship is there too)
        dx = towardX - o.x; dy = towardY - o.y; d = Math.hypot(dx, dy);
        if (d < 1e-4) { dx = 1; dy = 0; d = 1; }
      }
      px = o.x + (dx / d) * min;
      py = o.y + (dy / d) * min;
      moved = true;
    }
    px = Math.min(Math.max(px, inset), size - inset);
    py = Math.min(Math.max(py, inset), size - inset);
    if (!moved) break;
  }
  return { x: px, y: py };
}

/** A coarse navigation grid: `n`×`n` cells of `cell` metres; `blocked[gy*n+gx]` = 1 when un-sailable. */
export interface NavGrid { n: number; cell: number; blocked: Uint8Array }

/**
 * Rasterise the inflated obstacle set onto a coarse grid. CONSERVATIVE: a cell is blocked when its SQUARE
 * intersects any circle inflated by `clearance` (closest-point test), so every free cell's full square is
 * provably clear — the guarantee smoothing relies on (see module header).
 */
export function buildNavGrid(obstacles: readonly Obstacle[], clearance: number, size: number, cell: number): NavGrid {
  const n = Math.max(1, Math.ceil(size / cell));
  const blocked = new Uint8Array(n * n);
  for (let i = 0; i < obstacles.length; i++) {
    const o = obstacles[i]!;
    const R = o.r + clearance;
    const gx0 = Math.max(0, Math.floor((o.x - R) / cell));
    const gx1 = Math.min(n - 1, Math.floor((o.x + R) / cell));
    const gy0 = Math.max(0, Math.floor((o.y - R) / cell));
    const gy1 = Math.min(n - 1, Math.floor((o.y + R) / cell));
    for (let gy = gy0; gy <= gy1; gy++) {
      for (let gx = gx0; gx <= gx1; gx++) {
        // closest point of the cell square to the circle centre
        const cx = Math.min(Math.max(o.x, gx * cell), (gx + 1) * cell);
        const cy = Math.min(Math.max(o.y, gy * cell), (gy + 1) * cell);
        if ((cx - o.x) ** 2 + (cy - o.y) ** 2 < R * R) blocked[gy * n + gx] = 1;
      }
    }
  }
  return { n, cell, blocked };
}

/** Nearest FREE cell to (gx,gy), searched over growing square rings; strict (d2, then index) tie-break for
 *  determinism. Returns -1 when the whole grid is blocked. */
function nearestFreeCell(grid: NavGrid, gx: number, gy: number): number {
  const { n, blocked } = grid;
  if (gx >= 0 && gy >= 0 && gx < n && gy < n && !blocked[gy * n + gx]) return gy * n + gx;
  for (let r = 1; r < n; r++) {
    let best = -1, bestD2 = Infinity;
    const consider = (x: number, y: number): void => {
      if (x < 0 || y < 0 || x >= n || y >= n || blocked[y * n + x]) return;
      const d2 = (x - gx) * (x - gx) + (y - gy) * (y - gy);
      const idx = y * n + x;
      if (d2 < bestD2 || (d2 === bestD2 && idx < best)) { best = idx; bestD2 = d2; }
    };
    for (let dy = -r; dy <= r; dy++) {
      if (Math.abs(dy) === r) { for (let dx = -r; dx <= r; dx++) consider(gx + dx, gy + dy); } // ring caps: full rows
      else { consider(gx - r, gy + dy); consider(gx + r, gy + dy); }                            // ring sides: edges only
    }
    if (best >= 0) return best;
  }
  return -1;
}

const SQRT2 = Math.SQRT2;
// Fixed neighbor order (determinism): orthogonals first, then diagonals.
const NBR: readonly [number, number, number][] = [
  [1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1],
  [1, 1, SQRT2], [1, -1, SQRT2], [-1, 1, SQRT2], [-1, -1, SQRT2],
];

/**
 * Plan a route start→goal around the obstacle circles. Returns SPARSE waypoints — excluding the start,
 * including the exact goal — or `null` when the goal is unreachable (sealed off). Fast path: open water
 * straight to the goal is a single leg, no grid touched. Otherwise A* over `grid` (octile heuristic, no
 * corner-cutting) then greedy line-of-sight string-pulling against the exact circles.
 */
export function planRoute(
  sx: number, sy: number, gx: number, gy: number,
  grid: NavGrid, obstacles: readonly Obstacle[], clearance: number,
): NavPoint[] | null {
  if (segmentClearFrom(sx, sy, gx, gy, obstacles, clearance)) return [{ x: gx, y: gy }];

  const { n, cell, blocked } = grid;
  const cellOf = (x: number, y: number): [number, number] => [
    Math.min(n - 1, Math.max(0, Math.floor(x / cell))),
    Math.min(n - 1, Math.max(0, Math.floor(y / cell))),
  ];
  const [scx, scy] = cellOf(sx, sy);
  const [tcx, tcy] = cellOf(gx, gy);
  const start = nearestFreeCell(grid, scx, scy);
  const goal = nearestFreeCell(grid, tcx, tcy);
  if (start < 0 || goal < 0) return null;

  // --- A* (binary heap keyed (f, h, idx) — strict deterministic ordering) ---
  const gCost = new Float64Array(n * n).fill(Infinity);
  const came = new Int32Array(n * n).fill(-1);
  const closed = new Uint8Array(n * n);
  const gxOf = (i: number): number => i % n, gyOf = (i: number): number => (i / n) | 0;
  const goalX = gxOf(goal), goalY = gyOf(goal);
  const hOf = (i: number): number => {
    const dx = Math.abs(gxOf(i) - goalX), dy = Math.abs(gyOf(i) - goalY);
    return (dx + dy) + (SQRT2 - 2) * Math.min(dx, dy); // octile (admissible)
  };
  // heap of node indices; ranked by (f, h, idx)
  const heap: number[] = [];
  const fs = new Float64Array(n * n).fill(Infinity);
  const less = (a: number, b: number): boolean =>
    fs[a]! < fs[b]! || (fs[a]! === fs[b]! && (gCost[a]! > gCost[b]! || (gCost[a]! === gCost[b]! && a < b)));
  const push = (i: number): void => {
    heap.push(i);
    let c = heap.length - 1;
    while (c > 0) { const p = (c - 1) >> 1; if (less(heap[c]!, heap[p]!)) { const t = heap[c]!; heap[c] = heap[p]!; heap[p] = t; c = p; } else break; }
  };
  const pop = (): number => {
    const top = heap[0]!;
    const last = heap.pop()!;
    if (heap.length) {
      heap[0] = last;
      let c = 0;
      for (;;) {
        const l = 2 * c + 1, r = l + 1;
        let m = c;
        if (l < heap.length && less(heap[l]!, heap[m]!)) m = l;
        if (r < heap.length && less(heap[r]!, heap[m]!)) m = r;
        if (m === c) break;
        const t = heap[c]!; heap[c] = heap[m]!; heap[m] = t; c = m;
      }
    }
    return top;
  };

  gCost[start] = 0; fs[start] = hOf(start); push(start);
  let found = false;
  while (heap.length) {
    const cur = pop();
    if (closed[cur]) continue;
    closed[cur] = 1;
    if (cur === goal) { found = true; break; }
    const cx = gxOf(cur), cy = gyOf(cur);
    for (let k = 0; k < NBR.length; k++) {
      const [dx, dy, w] = NBR[k]!;
      const nx = cx + dx, ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= n || ny >= n) continue;
      const ni = ny * n + nx;
      if (blocked[ni] || closed[ni]) continue;
      // no corner cutting: a diagonal move needs both flanking orthogonal cells free
      if (dx !== 0 && dy !== 0 && (blocked[cy * n + nx] || blocked[ny * n + cx])) continue;
      const cand = gCost[cur]! + w;
      if (cand < gCost[ni]!) {
        gCost[ni] = cand;
        came[ni] = cur;
        fs[ni] = cand + hOf(ni);
        push(ni); // lazy-decrease (stale copies are skipped by `closed`)
      }
    }
  }
  if (!found) return null;

  // --- reconstruct (cell centres) + exact endpoints ---
  const raw: NavPoint[] = [];
  for (let i = goal; i >= 0; i = came[i]!) raw.push({ x: (gxOf(i) + 0.5) * cell, y: (gyOf(i) + 0.5) * cell });
  raw.reverse();
  raw.push({ x: gx, y: gy }); // finish on the exact (water-projected) goal

  // --- string-pulling: from the true start, greedily take the furthest point with a PROVEN-clear leg ---
  const out: NavPoint[] = [];
  let cx0 = sx, cy0 = sy, i0 = 0;
  while (i0 < raw.length) {
    let j = i0;
    for (let k = raw.length - 1; k > i0; k--) {
      if (segmentClearFrom(cx0, cy0, raw[k]!.x, raw[k]!.y, obstacles, clearance)) { j = k; break; }
    }
    // conservative rasterisation guarantees the hop to the NEXT free-cell centre is clear; still, if even
    // that fails (start pinned inside a margin), take it anyway — the A* topology leads out of the pin.
    out.push(raw[j]!);
    cx0 = raw[j]!.x; cy0 = raw[j]!.y;
    i0 = j + 1;
  }
  return out;
}
